import React, { useState, useRef, useEffect } from 'react';
import { Input } from 'antd';
import SpecDrawer from '../SpecDrawer/SpecDrawer';
import AccentButton from '../AccentButton/AccentButton';
import EditingBar from '../EditingBar/EditingBar';
import { DefaultAvatar, CameraIcon } from '../icons';
import { containsProfanity } from '../../utils/profanityFilter';
import type { ShowContext } from '../Comment/AddCommentDrawer';
import './AddPhotoDrawer.css';

export interface AddPhotoDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (photo: { file: File; previewUrl: string; userName?: string }) => void;
  showContext: ShowContext;
  isLoggedIn?: boolean;
  currentUser?: { name: string; avatarUrl?: string };
  selectedFile: File | null;
  previewUrl: string | null;
}

export const AddPhotoDrawer: React.FC<AddPhotoDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  showContext,
  isLoggedIn = false,
  currentUser,
  selectedFile,
  previewUrl,
}) => {
  const [guestName, setGuestName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [flashName, setFlashName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setGuestName('');
      setNameError(null);
      setFlashName(false);
    }
  }, [open]);

  const handleNameChange = (val: string) => {
    setGuestName(val);
    if (val.trim() && containsProfanity(val)) {
      setNameError('Name contains inappropriate language');
    } else {
      setNameError(null);
    }
  };

  const handleSubmit = () => {
    if (!selectedFile || !previewUrl) return;

    const missingName = !isLoggedIn && guestName.trim().length === 0;
    if (missingName || nameError) {
      if (missingName) {
        setFlashName(true);
        setTimeout(() => setFlashName(false), 1000);
        nameInputRef.current?.focus();
      }
      return;
    }

    onSubmit({
      file: selectedFile,
      previewUrl,
      userName: isLoggedIn ? undefined : guestName.trim(),
    });
  };

  const drawerTitle = (
    <div className="add-photo-drawer__header">
      <div className="add-photo-drawer__drag-handle" />
      <EditingBar
        icon={<CameraIcon size="m" tone="white" />}
        title="Adding Photo"
        onCancel={onClose}
      />
    </div>
  );

  return (
    <SpecDrawer
      title={drawerTitle}
      open={open}
      onClose={onClose}
      size="default"
      swipeable={false}
      closable={false}
      zIndex={1200}
    >
      <div className="add-photo-drawer__body">
        {!previewUrl && (
          <div className="add-photo-drawer__uploading">
            <div className="add-photo-drawer__uploading-dots">
              <span /><span /><span />
            </div>
            <span className="add-photo-drawer__uploading-text">Uploading...</span>
          </div>
        )}

        {previewUrl && (
          <div className="add-photo-drawer__content-row">
            <div className="add-photo-drawer__preview">
              <img src={previewUrl} alt="Selected" className="add-photo-drawer__preview-img" />
            </div>

            <div className="add-photo-drawer__content-right">
              {!isLoggedIn && (
                <div className="add-photo-drawer__field">
                  <label className="add-photo-drawer__label">Name Required</label>
                  <Input
                    ref={nameInputRef as any}
                    placeholder="Enter Name"
                    value={guestName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
                    maxLength={40}
                    status={nameError ? 'error' : undefined}
                    className={flashName ? 'add-photo-drawer__flash' : undefined}
                  />
                  {nameError && (
                    <span className="add-photo-drawer__error">{nameError}</span>
                  )}
                </div>
              )}

              {isLoggedIn && currentUser && (
                <div className="add-photo-drawer__field">
                  <label className="add-photo-drawer__label">Posting as</label>
                  <div className="add-photo-drawer__user-preview">
                    <div className="add-photo-drawer__avatar">
                      {currentUser.avatarUrl ? (
                        <img src={currentUser.avatarUrl} alt={currentUser.name} />
                      ) : (
                        <DefaultAvatar size={32} />
                      )}
                    </div>
                    <span className="add-photo-drawer__user-name">{currentUser.name}</span>
                  </div>
                </div>
              )}

              <AccentButton
                size="large"
                shape="round"
                onClick={handleSubmit}
                block
              >
                Post Now
              </AccentButton>
            </div>
          </div>
        )}
      </div>
    </SpecDrawer>
  );
};

export default AddPhotoDrawer;
